import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Image,
  SimpleGrid,
  VStack,
  Heading,
  Text,
  Container,
  Flex,
  Center,
  HStack,
} from "@chakra-ui/react";

import {
  useWallet,
  useAnchorWallet,
  AnchorWallet,
} from "@solana/wallet-adapter-react";

import axios from "axios";

import { Metadata, MetadataData } from "@metaplex/js";

import * as anchor from "@project-serum/anchor";

import {
  getMonsterFromWallet,
  createMonsterFight,
  fetchMonsterAbility,
  createMonster,
  getOwnedMetadata,
} from "./MonsterProgram";

import { Monster, Ability, MetadataType } from "./MonsterProgram";

import { Link } from "react-router-dom";

import slime1 from "./img/66.png";

export interface HomeProps {
  connection: anchor.web3.Connection;
}

interface AbilityDisplayProps {
  abilitySlot: number;
  connection: anchor.web3.Connection;
  anchorWallet: AnchorWallet;
}

interface MonsterDisplayProps {
  connection: anchor.web3.Connection;
  anchorWallet: AnchorWallet;
  children?: JSX.Element;
}

export const MonsterCard = (props: Monster) => {
  interface StatsDisplayProps {
    statName: string;
    value: number | string;
  }

  const StatsDisplay = ({ statName, value }: StatsDisplayProps) => {
    return (
      <Flex justifyContent="space-between" bg="gray.100">
        <Box>{statName}</Box>
        <Box>{value}</Box>
      </Flex>
    );
  };

  const MonsterStats: StatsDisplayProps[] = [
    {
      statName: "Level",
      value: props.level,
    },
    {
      statName: "Max HP",
      value: props.maxHp,
    },
    {
      statName: "Max MP",
      value: props.maxMana,
    },
    {
      statName: "Attack",
      value: props.attack,
    },
    {
      statName: "Defense",
      value: props.defense,
    },
  ];

  return (
    <Flex maxW="container.md" w="300px">
      <Flex h="full" w="50%" flexFlow="column" alignItems="center">
        <Image src={slime1} />
      </Flex>
      <Flex h="full" w="50%" flexFlow="column" mx={2}>
        <Flex alignItems="center">
          <Heading>{props.name}</Heading>
        </Flex>

        {MonsterStats.map((stat, idx) => (
          <StatsDisplay key={idx} statName={stat.statName} value={stat.value} />
        ))}
      </Flex>
    </Flex>
  );
};

export const AbilityDisplay = (props: AbilityDisplayProps) => {
  const [ability, setAbility] = useState<Ability>();

  useEffect(() => {
    async function getAbility() {
      let ability = await fetchMonsterAbility(
        props.anchorWallet as anchor.Wallet,
        props.connection,
        props.abilitySlot
      );

      setAbility(ability);
    }

    getAbility();
  }, [props.connection, props.abilitySlot, props.anchorWallet]);

  if (!ability) {
    return <Box>No Ability</Box>;
  }

  return <Box>{ability.name}</Box>;
};

interface MonsterAbilityDisplayProps {
  connection: anchor.web3.Connection;
  anchorWallet: AnchorWallet;
  monster: Monster;
}

export const MonsterAbilityDisplay = (props: MonsterAbilityDisplayProps) => {
  return (
    <Box maxW="200" w="100%" borderWidth="1px" borderRadius="lg">
      <VStack>
        <Heading>Abilities</Heading>
        <Link to="/ability">
          <Button>View Abilities</Button>
        </Link>
        <AbilityDisplay {...props} abilitySlot={1} />
        <AbilityDisplay {...props} abilitySlot={2} />
        <AbilityDisplay {...props} abilitySlot={3} />
        <AbilityDisplay {...props} abilitySlot={4} />
      </VStack>
    </Box>
  );
};

interface CreateFightDisplayProps {
  connection: anchor.web3.Connection;
  anchorWallet: AnchorWallet;
}

const CreateFightDisplay = (props: CreateFightDisplayProps) => {
  const [createdFight, setCreatedFight] = useState<anchor.web3.PublicKey>();

  async function createFightHandler() {
    let fight = await createMonsterFight(
      props.anchorWallet as anchor.Wallet,
      props.connection
    );
    setCreatedFight(fight);
  }

  return (
    <Box>
      {createdFight ? (
        <Box>
          <Box mb={8}>Your fight ID is: {createdFight.toString()}</Box>
          <Button>
            <Link to={"/fight/" + createdFight.toString()}>
              Enter the Arena
            </Link>
          </Button>
        </Box>
      ) : (
        <Button onClick={createFightHandler}>Create Fight</Button>
      )}
    </Box>
  );
};

interface MetaplexMetadataDisplayProps {
  data: MetadataData;
  info: object;
  pubkey: anchor.web3.PublicKey;
  connection: anchor.web3.Connection;
  anchorWallet: AnchorWallet;
  setMonster: React.Dispatch<React.SetStateAction<Monster>>;
}
export const MetaplexMetadataDisplay = ({
  data,
  connection,
  anchorWallet,
  setMonster,
}: MetaplexMetadataDisplayProps) => {
  const [image, setImage] = useState("");

  //get Image
  useEffect(() => {
    axios.get<MetadataType>(data.data.uri).then((res) => {
      console.log(res);

      setImage(res.data.image);
    });
  }, [data.data.uri]);

  return (
    <Flex flexFlow="column">
      {data.data.name}
      <Image mb={2} src={image} w="full" maxH="full" />
      <Button
        fontSize="0.6em"
        onClick={async () => {
          let monster = await createMonster(
            anchorWallet as anchor.Wallet,
            connection
          );
          setMonster(monster);
        }}
      >
        {" "}
        Create Monster
      </Button>
    </Flex>
  );
};

export const MonsterDisplay = (props: MonsterDisplayProps) => {
  const [monster, setMonster] = useState<Monster>();
  const [metadataList, setMetadataList] = useState<Metadata[]>();

  useEffect(() => {
    (async () => {
      if (props.anchorWallet.publicKey) {
        let monster = await getMonsterFromWallet(
          props.anchorWallet as anchor.Wallet,
          props.connection
        );

        setMonster(monster);
      }
    })();
  }, [props.anchorWallet, props.connection]);

  if (!monster) {
    return (
      <VStack justifyContent="center">
        <Text>
          Hey! Wait! Don't go out! It's unsafe! You need a monster for your own
          protection. Perhaps one of your NFT's?
        </Text>

        <Button
          onClick={async () => {
            setMetadataList(
              await getOwnedMetadata(
                props.anchorWallet as anchor.Wallet,
                props.connection
              )
            );
          }}
        >
          Load NFT's
        </Button>

        {metadataList && (
          <Box>
            Choose an NFT for your monster!
            {metadataList.length > 0 ? (
              <Box>
                <SimpleGrid columns={6} spacing={2}>
                  {metadataList.map((meta, idx) => (
                    <MetaplexMetadataDisplay
                      setMonster={setMonster}
                      anchorWallet={props.anchorWallet as anchor.Wallet}
                      connection={props.connection}
                      key={idx}
                      {...meta}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            ) : (
              <Box w="full" textAlign="center">
                We could not find any Metaplex NFT's that belong to you!
              </Box>
            )}
          </Box>
        )}

        {/* <Button
          onClick={async () => {
            setMonster(
              await createMonster(
                props.anchorWallet as anchor.Wallet,
                props.connection
              )
            );
          }}
        >
          Create Monster
        </Button> */}
      </VStack>
    );
  }

  return (
    <Flex w="full">
      <VStack w="full">
        <Heading>Profile</Heading>
        <MonsterProfile {...monster} />
      </VStack>
      <VStack w="full">
        <Heading>Arena</Heading>
        {props.children}
      </VStack>
    </Flex>
  );
};

export const MonsterProfile = (props: Monster) => {
  const [image, setImage] = useState("");

  useEffect(() => {
    axios.get<MetadataType>(props.uri).then((res) => {
      setImage(res.data.image);
    });
  }, [props.uri]);

  interface StatsDisplayProps {
    statName: string;
    value: number | string;
  }

  const StatsDisplay = ({ statName, value }: StatsDisplayProps) => {
    return (
      <Flex justifyContent="space-between">
        <Box>{statName}</Box>
        <Box>{value}</Box>
      </Flex>
    );
  };

  const MonsterStats: StatsDisplayProps[] = [
    {
      statName: "Level",
      value: props.level,
    },
    {
      statName: "Max HP",
      value: props.maxHp,
    },
    {
      statName: "Max MP",
      value: props.maxMana,
    },
    {
      statName: "Attack",
      value: props.attack,
    },
    {
      statName: "Defense",
      value: props.defense,
    },
  ];

  return (
    <Flex
      maxW="container.md"
      w="300px"
      alignItems="center"
      mx={4}
      p="2"
      border="2px solid"
      borderColor="white"
    >
      <Flex h="full" w="50%" flexFlow="column" alignItems="center">
        <Image src={image} />
      </Flex>
      <Flex h="full" w="50%" flexFlow="column" mx={2}>
        <Flex alignItems="center" justifyContent="center">
          <Heading fontSize="1em">{props.name}</Heading>
        </Flex>

        {MonsterStats.map((stat, idx) => (
          <StatsDisplay key={idx} statName={stat.statName} value={stat.value} />
        ))}
      </Flex>
    </Flex>
  );
};

const Home = (props: HomeProps) => {
  const wallet = useWallet();
  const anchorWallet = useAnchorWallet();

  return (
    <VStack mt="100px" justifyContent="center" mx="20%" textAlign="center">
      <Box mb="16px">
        <Heading>Welcome! </Heading>
        <Text>
          Make sure to add abilities to your monster before entering combat.
        </Text>
        {/* <Text>Feel free to check out our <a href="https://discord.gg/62Tnfb78">discord</a> and leave feedback.</Text> */}
      </Box>
      <MonsterDisplay connection={props.connection} anchorWallet={anchorWallet}>
        <CreateFightDisplay
          connection={props.connection}
          anchorWallet={anchorWallet}
        />
      </MonsterDisplay>
    </VStack>
  );
};

export default Home;
